/**
 * CMS-driven health citations & disclaimers (Guideline 1.4.1). Used by full screen + bottom sheets.
 */
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { appTheme, useThemeColors } from '../../shared/theme';

import { HealthInformationCmsHtmlView } from './HealthInformationCmsHtmlView';
import { useHealthInformationCms } from './HealthInformationCmsContext';

type HealthInformationScrollBodyProps = {
  /** When true, omit top margin on mandatory banner (e.g. inside modal). */
  compactTop?: boolean;
};

export function HealthInformationScrollBody({ compactTop: _compactTop = false }: HealthInformationScrollBodyProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  const { html, isLoading, isAvailable } = useHealthInformationCms();

  if (isLoading) {
    return (
      <View style={{ paddingVertical: appTheme.spacing.xl, alignItems: 'center' }}>
        <ActivityIndicator color={colors.primary} />
      </View>
    );
  }

  if (!isAvailable || !html) {
    return null;
  }

  return <HealthInformationCmsHtmlView html={html} style={{ minHeight: 240 }} />;
}

/** Bottom sheet / modal: bounded scroll area with safe-area padding at bottom. */
export function HealthInformationPopupScrollBody() {
  const insets = useSafeAreaInsets();
  const { isLoading, isAvailable } = useHealthInformationCms();

  if (!isLoading && !isAvailable) {
    return null;
  }

  return (
    <View
      style={{
        flex: 1,
        minHeight: 0,
        paddingHorizontal: appTheme.spacing.lg,
        paddingBottom: insets.bottom + appTheme.spacing.xl,
      }}>
      <HealthInformationScrollBody compactTop />
    </View>
  );
}
